// Self-contained client for deals-service — deal versioning. Mirrors
// documentsExtApi.ts: SAME shared `platform` cluster, base path /deals.
//
// Do not "correct" the host to deals.api.<apex> — per-service subdomains are
// closed (AWS 120-custom-domain cap).

import type { DealVersion, DealVersionChange, StaleDocument } from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveDealsHost(
  hostname: string,
  stage: 'prod' | 'staging' | 'dev',
  envBase?: string,
): string {
  if (envBase) return envBase;
  // Dev server proxies /deals-api → platform.api.<apiDevBase>/deals.
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/deals-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/deals`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/deals`;
  return `https://platform.api.dev.${apex}/deals`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_DEALS_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveDealsHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    let payload: any = null;
    try { payload = await res.json(); detail = payload?.error || ''; } catch { /* non-JSON body */ }
    // A publish refusal carries WHY — the failing fields, or the conflict. It
    // is thrown with the payload attached so the modal can show the list
    // rather than a bare status code.
    const err: any = new Error(detail || `deals ${res.status}`);
    err.status = res.status;
    err.payload = payload;
    throw err;
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export interface DealVersionRow extends Partial<DealVersion> {
  versionNo: number;
  status: 'draft' | 'published' | 'retired';
  effectiveAt?: string;
  publishedAt?: string;
  publishedBy?: string;
  changeType?: 'material' | 'minor';
}

/**
 * WHAT A DEAL CURRENTLY SELLS — the frozen commercial fields of its active
 * published version, or `implicit: true` when it has never published one.
 *
 * Every field below `hasDraft` is present ONLY when `implicit` is false. A
 * reader must branch on `implicit` rather than defaulting the fields, because
 * "published with no devices" and "never published" price completely
 * differently and only one of them is a contract.
 */
export interface ActiveDealVersionSummary {
  implicit: boolean;
  /** Unpublished work is waiting on this deal. Independent of `implicit`. */
  hasDraft: boolean;
  activeVersionNo: number | null;
  effectiveAt?: string;
  priceCents?: number;
  packageIds?: string[];
  packageDiscounts?: Record<string, unknown>;
  devicePackageIds?: string[];
  deviceTerms?: Record<string, any>;
  freeLocationQuota?: number;
  additionalLocationPriceCents?: number;
  commissionPlan?: { planId: string; name: string } | null;
}

export const dealsApi = {
  /**
   * `implicit: true` means this deal has never published a version — the deal
   * row itself is its v1. That is the normal state of every deal that existed
   * before versioning, not an error.
   */
  listVersions: (dealId: string) =>
    req<{ versions: DealVersionRow[]; activeVersionNo: number | null; implicit: boolean }>(
      `/deals/${encodeURIComponent(dealId)}/versions`,
    ),

  getVersion: (dealId: string, versionNo: number) =>
    req<{ version: DealVersion }>(`/deals/${encodeURIComponent(dealId)}/versions/${versionNo}`),

  /**
   * THE ACTIVE PUBLISHED VERSION OF A PAGE OF DEALS, IN ONE REQUEST.
   *
   * The deals list must quote what each deal actually SELLS, which is its
   * published version — the deal row is the working copy and may hold an
   * unpublished draft price. Asking per deal is one request per row, and a
   * master deal can have a thousand variants.
   *
   * A deal that has never published comes back as `{ implicit: true }` with no
   * commercial fields: the caller then reads the deal row as its implicit v1.
   * That absence is deliberate — filling it in from the row here would erase
   * the difference between "published at this price" and "never published".
   *
   * At most 100 ids per call; the list sends one page at a time.
   */
  activeVersions: (dealIds: string[]) =>
    req<{ versions: Record<string, ActiveDealVersionSummary> }>('/deals/active-versions', {
      method: 'POST', body: JSON.stringify({ dealIds }),
    }),

  saveDraft: (dealId: string, patch: Record<string, unknown>) =>
    req<{ draft: DealVersionRow }>(`/deals/${encodeURIComponent(dealId)}/versions/draft`, {
      method: 'PUT', body: JSON.stringify(patch),
    }),

  /**
   * THROW THE UNPUBLISHED DRAFT AWAY and go back to what is live.
   *
   * The inverse of `saveDraft`. The editor opens on the draft — which is right,
   * it is the operator's own work — so without this an edit somebody thought
   * better of followed them into every visit, and the only way out was to retype
   * the published values over it.
   *
   * 404 means there was nothing to discard; 409 means somebody published it
   * first. Neither is an error to retry — both want a reload.
   */
  discardDraft: (dealId: string) =>
    req<{ discardedVersionNo: number }>(`/deals/${encodeURIComponent(dealId)}/versions/draft`, {
      method: 'DELETE',
    }),

  publish: (dealId: string, body: { changeType: 'material' | 'minor'; effectiveAt?: string }) =>
    req<{ version: DealVersionRow }>(`/deals/${encodeURIComponent(dealId)}/versions/publish`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  diff: (dealId: string, versionNo: number, against: number) =>
    req<{ from: number; to: number; changes: DealVersionChange[] }>(
      `/deals/${encodeURIComponent(dealId)}/versions/${versionNo}/diff?against=${against}`,
    ),

  /** The document index is supplied by the caller — super-admin-ui already holds it. */
  stale: (dealId: string, index: Record<string, { activeVersionNo: number; materialAfter: number[] }>) =>
    req<{ stale: StaleDocument[]; implicit: boolean }>(
      `/deals/${encodeURIComponent(dealId)}/versions/stale?index=${encodeURIComponent(JSON.stringify(index))}`,
    ),
};

export default dealsApi;

// ─────────────────────────────────────────────────────────────────────────────
// DEAL INVITES — Command Center → Send Deal (deals-service /deals/invites).
//
// Declared locally rather than imported from shared-types: frontend CI builds
// against shared-types@development, and a brand-new export there would fail
// the build until it is merged. The server's schema is `DealInviteSchema`.
// ─────────────────────────────────────────────────────────────────────────────

export type DealInviteStage = 'sent' | 'opened' | 'signed_up' | 'expired' | 'revoked';

export interface DealInvite {
  inviteId: string;
  token: string;
  contactName: string;
  businessName: string;
  email: string;
  phone?: string;
  note?: string;
  dealId: string;
  dealCode: string;
  dealName: string;
  expiresAt?: number;
  status: 'sent' | 'revoked' | 'signed_up';
  stage: DealInviteStage;
  url: string;
  openCount?: number;
  firstOpenedAt?: number;
  lastOpenedAt?: number;
  signedUpAt?: number;
  tenantId?: string;
  leadId?: string;
  sentByEmail?: string;
  sentByName?: string;
  sendCount?: number;
  lastSentAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface CreateDealInviteBody {
  contactName: string;
  businessName: string;
  email: string;
  phone?: string;
  note?: string;
  dealId: string;
  expiresInDays?: number;
  leadId?: string;
  sendEmail?: boolean;
}

export const dealInvitesApi = {
  list: () => req<{ invites: DealInvite[]; count: number }>('/deals/invites'),
  create: (body: CreateDealInviteBody) =>
    req<{ invite: DealInvite; emailQueued: boolean }>('/deals/invites', { method: 'POST', body: JSON.stringify(body) }),
  resend: (inviteId: string) =>
    req<{ invite: DealInvite; emailQueued: boolean }>(`/deals/invites/${encodeURIComponent(inviteId)}/resend`, { method: 'POST' }),
  revoke: (inviteId: string) =>
    req<{ invite: DealInvite }>(`/deals/invites/${encodeURIComponent(inviteId)}/revoke`, { method: 'POST' }),
};
